import {
  closestCenter,
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers';
import {
  arrayMove,
  horizontalListSortingStrategy,
  SortableContext,
  useSortable,
} from '@dnd-kit/sortable';
import { Skeleton, Table } from 'antd';
import Cookies from 'js-cookie';
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useMpProductColumns } from './columns';

const DragIndexContext = createContext({ active: -1, over: -1 });

const COLUMN_ORDER_COOKIE_NAME = 'mpProductListColumnOrder';
const COOKIE_EXPIRES_DAYS = 30;

// Style pour les colonnes pendant le drag
const dragActiveStyle = (dragState, id) => {
  const { active, over } = dragState;
  let style = {};
  if (active && active === id) {
    style = { backgroundColor: 'rgba(0, 0, 0, 0.05)', opacity: 0.5 };
  } else if (over && id === over && active !== over) {
    style = { borderInlineStart: '1px dashed #1890ff' };
  }
  return style;
};

const TableBodyCell = (props) => {
  const dragState = useContext(DragIndexContext);
  return (
    <td
      {...props}
      style={{ ...props.style, ...dragActiveStyle(dragState, props.id) }}
    />
  );
};

const TableHeaderCell = (props) => {
  const dragState = useContext(DragIndexContext);
  const { attributes, listeners, setNodeRef, isDragging } = useSortable({
    id: props.id,
  });
  const style = {
    ...props.style,
    cursor: 'move',
    ...(isDragging
      ? { position: 'relative', zIndex: 9999, userSelect: 'none' }
      : {}),
    ...dragActiveStyle(dragState, props.id),
  };
  return (
    <th
      {...props}
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
    />
  );
};

const MpProductTable = ({
  hasSelection = true,
  hasActions = true,
  stickyHeader = true,
  mpCatalogues,
  count,
  isLoading,
  currentPage = 1,
  pageSize = 20,
  listHeight = 500,
  onPageChange = () => {},
  onPageSizeChange = () => {},
  selectedProducts = [],
  updateSelectedMpProducts,
  onRemoveOneFromCategory = () => {},
  onPublishOne = () => {},
  onTransformOne = () => {},
}) => {
  const baseColumns = useMpProductColumns({
    hasActions,
    onRemoveOneFromCategory,
    onPublishOne,
    onTransformOne,
  });

  // État pour gérer le drag des colonnes
  const [dragIndex, setDragIndex] = useState({ active: -1, over: -1 });
  // Initialiser les colonnes avec l'ordre sauvegardé dans les cookies
  const [columns, setColumns] = useState(() => {
    const columnsWithKeys = baseColumns.map((column, i) => ({
      ...column,
      key: column.key || `col-${i}`,
      onHeaderCell: () => ({ id: column.key || `col-${i}` }),
      onCell: () => ({ id: column.key || `col-${i}` }),
    }));

    const savedOrder = Cookies.get(COLUMN_ORDER_COOKIE_NAME);
    if (savedOrder) {
      try {
        const orderArray = JSON.parse(savedOrder);
        const orderedColumns = orderArray
          .map((key) => columnsWithKeys.find((col) => col.key === key))
          .filter(Boolean);

        const newColumns = columnsWithKeys.filter(
          (col) => !orderArray.includes(col.key)
        );

        return [...orderedColumns, ...newColumns];
      } catch (error) {
        console.error('Erreur lors de la lecture des cookies:', error);
        return columnsWithKeys;
      }
    }

    return columnsWithKeys;
  });

  useEffect(() => {
    const newBaseColumns = baseColumns.map((column, i) => ({
      ...column,
      key: column.key || `col-${i}`,
      onHeaderCell: () => ({ id: column.key || `col-${i}` }),
      onCell: () => ({ id: column.key || `col-${i}` }),
    }));

    setColumns((prevColumns) => {
      // Préserver l'ordre actuel des colonnes
      const currentOrder = prevColumns.map((col) => col.key);
      const orderedColumns = currentOrder
        .map((key) => newBaseColumns.find((col) => col.key === key))
        .filter(Boolean);

      // Ajouter les nouvelles colonnes qui n'existaient pas avant
      const newColumns = newBaseColumns.filter(
        (col) => !currentOrder.includes(col.key)
      );

      const result = [...orderedColumns, ...newColumns];

      // Éviter les mises à jour inutiles si l'ordre n'a pas changé
      if (
        JSON.stringify(result.map((c) => c.key)) ===
        JSON.stringify(prevColumns.map((c) => c.key))
      ) {
        return prevColumns;
      }

      return result;
    });
  }, [baseColumns]);

  // Sauvegarder l'ordre des colonnes dans les cookies
  useEffect(() => {
    const columnOrder = columns.map((col) => col.key);
    const columnOrderStr = JSON.stringify(columnOrder);
    const savedOrder = Cookies.get(COLUMN_ORDER_COOKIE_NAME);

    // Ne sauvegarder que si l'ordre a vraiment changé
    if (columnOrderStr !== savedOrder) {
      Cookies.set(COLUMN_ORDER_COOKIE_NAME, columnOrderStr, {
        expires: COOKIE_EXPIRES_DAYS,
      });
    }
  }, [columns]);

  // Extraire les IDs des produits sélectionnés pour les selectedRowKeys
  const selectedRowKeys = useMemo(
    () => selectedProducts.map((product) => product?.id),
    [selectedProducts]
  );

  // Configuration de la sélection des lignes
  const rowSelection = {
    selectedRowKeys,
    onChange: (newSelectedRowKeys, _selectedRows) => {
      // Récupérer les IDs des produits de la page courante
      const currentPageIds = mpCatalogues.map((p) => p.id);

      // Garder les produits sélectionnés qui ne sont PAS sur la page courante
      const productsFromOtherPages = selectedProducts.filter(
        (product) => !currentPageIds.includes(product.id)
      );

      // Ajouter les produits sélectionnés de la page courante
      const selectedProductsFromCurrentPage = mpCatalogues.filter((product) =>
        newSelectedRowKeys.includes(product.id)
      );

      // Combiner les deux : produits des autres pages + sélection actuelle
      const updatedProducts = [
        ...productsFromOtherPages,
        ...selectedProductsFromCurrentPage,
      ];

      // Passer les données complètes des produits sélectionnés
      updateSelectedMpProducts(updatedProducts);
    },
    preserveSelectedRowKeys: true,
    getCheckboxProps: (record) => ({
      name: record.id,
    }),
  };

  // Configuration des sensors pour le drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 1,
      },
    })
  );

  const onDragEnd = ({ active, over }) => {
    if (active.id !== over?.id) {
      setColumns((prevState) => {
        const activeIndex = prevState.findIndex((i) => i.key === active?.id);
        const overIndex = prevState.findIndex((i) => i.key === over?.id);
        return arrayMove(prevState, activeIndex, overIndex);
      });
    }
    setDragIndex({ active: -1, over: -1 });
  };

  const onDragOver = ({ active, over }) => {
    const activeIndex = columns.findIndex((i) => i.key === active.id);
    const overIndex = columns.findIndex((i) => i.key === over?.id);
    setDragIndex({
      active: active.id,
      over: over?.id,
      direction: overIndex > activeIndex ? 'right' : 'left',
    });
  };

  if (isLoading) {
    return (
      <Table
        size="small"
        columns={columns.map((column) => ({
          ...column,
          render: () => (
            <Skeleton.Input
              active
              style={{ width: column.width || 120 }}
              size="small"
            />
          ),
        }))}
        dataSource={Array(10)
          .fill(null)
          .map((_, index) => ({ key: index }))}
        pagination={false}
        scroll={{ x: 'max-content', y: 550 }}
        sticky={stickyHeader ? { offsetHeader: 64 } : false}
      />
    );
  }

  return (
    <DndContext
      sensors={sensors}
      modifiers={[restrictToHorizontalAxis]}
      onDragEnd={onDragEnd}
      onDragOver={onDragOver}
      collisionDetection={closestCenter}
    >
      <SortableContext
        items={columns.map((i) => i.key)}
        strategy={horizontalListSortingStrategy}
      >
        <DragIndexContext.Provider value={dragIndex}>
          <Table
            columns={columns}
            dataSource={mpCatalogues}
            rowKey="id"
            size="small"
            rowSelection={hasSelection ? rowSelection : undefined}
            pagination={{
              position: ['bottomRight'],
              current: currentPage,
              pageSize: pageSize,
              total: count,
              showSizeChanger: true,
              pageSizeOptions: ['20', '50', '100', '200'],
              showQuickJumper: false,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} sur ${count} produits`,
              onChange: (page, size) => {
                if (size === pageSize) {
                  onPageChange(page);
                }
              },
              onShowSizeChange: (_, size) => {
                onPageSizeChange(size);
                onPageChange(1); // Reset to first page when changing page size
              },
            }}
            scroll={{ x: 'max-content', y: listHeight }}
            sticky={stickyHeader ? { offsetHeader: 64 } : false}
            components={{
              header: { cell: TableHeaderCell },
              body: { cell: TableBodyCell },
            }}
          />
        </DragIndexContext.Provider>
      </SortableContext>
      <DragOverlay>
        <div
          style={{
            backgroundColor: 'rgba(24, 144, 255, 0.1)',
            padding: 16,
            border: '1px solid #1890ff',
            borderRadius: 4,
            fontWeight: 500,
          }}
        >
          {columns[columns.findIndex((i) => i.key === dragIndex.active)]?.title}
        </div>
      </DragOverlay>
    </DndContext>
  );
};

export default MpProductTable;
